import type {
  LandingFeatureAction,
  LandingFeatureHeadingSegment,
} from '../components/landing-feature-section/landing-feature-section';
import type {
  LandingHeroAction,
  LandingHeroTextSegment,
} from '../components/landing-hero/landing-hero';
import type { CarouselImage } from '../components/image-carousel/image-carousel';

export type LandingHeroContent = Readonly<{
  heading: string;
  leadSegments: readonly LandingHeroTextSegment[];
  bodyText: string;
  imagePath: string;
  imageAlt: string;
  primaryAction?: LandingHeroAction | null;
  secondaryAction?: LandingHeroAction | null;
}>;

export type LandingValueCard = Readonly<{
  title: string;
  subtitle: string;
  text: string;
}>;

export type LandingPricingCard = Readonly<{
  planName: string;
  price: string;
  pricePrefix?: string;
  priceSuffix?: string;
  features: readonly string[];
  bestFor: string;
  ctaLabel: string;
  ctaRouterLink?: string;
  ctaFragment?: string;
}>;

export type LandingFeatureContent = Readonly<{
  headingId: string;
  headingSegments: readonly LandingFeatureHeadingSegment[];
  leadText: string;
  bodyText?: string;
  primaryAction?: LandingFeatureAction | null;
  secondaryAction?: LandingFeatureAction | null;
  showTopBorder?: boolean;
  showBottomBorder?: boolean;
  showVisual?: boolean;
  carouselImages?: readonly CarouselImage[] | null;
}>;

export const LANDING_HERO: LandingHeroContent = {
  heading: 'Don’t gamble with your cloud costs.',
  leadSegments: [
    { text: 'Sentinel gives AI/ML teams the ' },
    { text: 'visibility, alerts,', accent: true },
    { text: ' and ' },
    { text: 'controls', accent: true },
    { text: ' they need to ' },
    { text: 'right-size cloud usage', accent: true },
    { text: ' across jobs, projects, and teams.' },
  ],
  bodyText:
    'AI/ML workloads are expensive, spiky, and hard to predict. Sentinel turns workload telemetry into dashboards, proactive alerts, and governance workflows so teams can catch over-provisioning, track savings, and keep infrastructure decisions under control.',
  imagePath: 'assets/images/dice-cubes.webp',
  imageAlt: 'Three branded dice representing cloud cost decisions',
  primaryAction: {
    label: 'Get started for free',
    register: true,
  },
  secondaryAction: {
    label: 'Sentinel overview and plans',
    routerLink: '/plans',
  },
};

export const GUESSWORK_SECTION: LandingFeatureContent = {
  headingId: 'landing-guesswork-title',
  headingSegments: [
    { text: 'Stop managing', accent: true },
    { text: ' AI/ML infrastructure ' },
    { text: 'by', accent: true },
    { text: ' guesswork' },
    { text: '.', accent: true },
  ],
  leadText:
    'Before Sentinel, teams often rely on manual CPU, memory, and GPU allocations. Over-provisioning becomes the safe default, leaders lack workload-level cost visibility, and waste is discovered only after the bill arrives.',
  bodyText:
    'Sentinel gives teams a shared control plane for AI/ML cloud usage, with central dashboards, near real-time reporting, proactive alerts, historical savings analysis, and team-level policies.',
  carouselImages: [
    {
      src: 'assets/images/sentinel_screenshots/sentinel-screenshot-1.webp',
      alt: 'Sentinel Run Groups table with bar charts',
    },
    {
      src: 'assets/images/sentinel_screenshots/sentinel-screenshot-2.webp',
      alt: 'Sentinel Run Groups table with bar charts',
    },
    {
      src: 'assets/images/sentinel_screenshots/sentinel-screenshot-3.webp',
      alt: 'Sentinel Recent workflow runs with sparkline cards',
    },
    {
      src: 'assets/images/sentinel_screenshots/sentinel-screenshot-4.webp',
      alt: 'Sentinel Recent workflow runs with sparkline cards',
    },
    {
      src: 'assets/images/sentinel_screenshots/sentinel-screenshot-5.webp',
      alt: 'Sentinel Run details page with statistics',
    },
    {
      src: 'assets/images/sentinel_screenshots/sentinel-screenshot-6.webp',
      alt: 'Sentinel Run details page with sparkline charts',
    },
    {
      src: 'assets/images/sentinel_screenshots/sentinel-screenshot-7.webp',
      alt: 'Sentinel Run details page with sparkline charts',
    },
  ],
};

export const CONTROL_PLANE_SECTION: LandingFeatureContent = {
  headingId: 'landing-control-plane-title',
  headingSegments: [
    { text: 'Sentinel is the', accent: true },
    { text: ' control plane ' },
    { text: 'for', accent: true },
    { text: ' AI/ML ' },
    { text: 'cloud', accent: true },
    { text: ' cost governance.' },
  ],
  leadText:
    'It turns workload telemetry and infrastructure recommendations into dashboards, alerts, policies, and savings analysis.',
  bodyText:
    'Start free with Sentinel Access, then upgrade when you need team dashboards, longer retention, centralized controls, SSO/RBAC, or enterprise deployment options.',
  primaryAction: {
    label: 'Start for free with Sentinel Access',
    register: true,
  },
  secondaryAction: {
    label: 'Talk to us about team plans',
  },
  showTopBorder: false,
  showBottomBorder: true,
};

export const READY_TO_STOP_GUESSING_SECTION: LandingFeatureContent = {
  headingId: 'landing-ready-title',
  headingSegments: [{ text: 'Ready to stop guessing?', accent: true }],
  leadText:
    'Start free with Sentinel Access, or talk to us about\nteam-wide AI/ML cloud cost governance.',
  primaryAction: {
    label: 'Start for free with Sentinel Access',
    register: true,
  },
  secondaryAction: {
    label: 'Talk to us about team plans',
  },
  showTopBorder: false,
  showBottomBorder: true,
  showVisual: false,
};

export const LANDING_VALUE_CARDS: readonly LandingValueCard[] = [
  {
    title: 'Know',
    subtitle: 'See what AI/ML jobs\nactually use',
    text: 'Track CPU, GPU, memory, runtime, and cost signals across AI/ML jobs. Move from rough allocation guesses to measured usage patterns.',
  },
  {
    title: 'Watch',
    subtitle: 'Catch waste\nearly',
    text: 'Use proactive alerts to detect over-provisioning, cost anomalies, drift, and budget threshold issues before they become persistent spend.',
  },
  {
    title: 'Apply',
    subtitle: 'Govern team\nusage',
    text: 'Apply central policies, organize reporting by user, team, project, or tag, and align infrastructure usage with cost and performance goals.',
  },
  {
    title: 'Track',
    subtitle: 'Track savings\nover time',
    text: 'Measure realized improvements from right-sizing decisions, policy changes, and better infrastructure choices.',
  },
];

export const LANDING_PRICING_CARDS: readonly LandingPricingCard[] = [
  {
    planName: 'Sentinel Access',
    price: 'Free',
    features: [
      'Single-user visibility',
      'Automated reports',
      'Right-sizing insights',
      'Short-term history',
    ],
    bestFor: 'Individuals tracking and optimizing their own AI/ML jobs',
    ctaLabel: 'Learn more about Sentinel Access',
    ctaRouterLink: '/plans',
    ctaFragment: 'sentinel-access',
  },
  {
    planName: 'Sentinel Achiever',
    price: '$1,000',
    pricePrefix: 'From',
    priceSuffix: '/month',
    features: ['Team dashboards', 'Alerts, automations', 'Savings tracking', 'Extended retention'],
    bestFor: 'Growing teams to cut their $5,000 monthly cloud spend',
    ctaLabel: 'Learn more about Sentinel Achiever',
    ctaRouterLink: '/plans',
    ctaFragment: 'sentinel-achiever',
  },
  {
    planName: 'Sentinel Champion',
    price: '$2,500',
    pricePrefix: 'From',
    priceSuffix: '/month',
    features: ['Enterprise controls', 'SSO, RBAC, SLA', 'Custom policies', 'Cloud or on-prem'],
    bestFor: 'Enterprises demanding strict security and compliance',
    ctaLabel: 'Learn more about Sentinel Champion',
    ctaRouterLink: '/plans',
    ctaFragment: 'sentinel-champion',
  },
];
